import React, { useEffect, useState, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash.get';
import includes from 'lodash.includes';
import { useHistory, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import axios from 'axios';
import { useQuery } from '@panel/commons/hooks';
import { currencyOptionsBySite, site } from '@panel/commons/helpers';
import { devLogger } from '@panel/commons/helpers/devLogger';
import { Step1Module1Selectors } from '../../../../../ducks/step1Module1';
import { Step4Module1Selectors } from '../../../../../ducks/step4Module1';
import {
  getEcommerceUser,
  getProductTaxes,
  selectProductTaxesParams,
  selectCurrentProduct,
  selectProductTaxes,
  selectBillingInfo,
  selectEcommerceUser,
  setPaymentLoading
} from '../../../../../ducks/step4Module2';
import BillingSection from './billing/billingSection';
import PaymentSection from './payment/paymentSection';
import { useStepperCtx } from '../../../../stepper';
import { sendEvent } from '../../../../../utils';
import { usePublisherRedirection } from '../../../../../hooks/usePublisherRedirection';
import { FORM_ABANDON_EVENT, track } from '../../../../../utils/trackingEvent';
import { getWithUpgrade } from '../../../../../utils/misc';
import { getIdentificationTypes } from '../../../../../utils/identificationTypes';
import {
  MPErrorCodes,
  initialFormAttributes,
  headers,
  setIssuerFromResponse,
  getBillingInfo,
  sendCheckoutDataLayer
} from './utils';

const API_ECOM = process.env.REACT_APP_API_ECOMMERCE;
const siteId = site.id;

function PaymentForm() {
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const dispatch = useDispatch();
  const { push } = useHistory();
  const { handleSubmit, setError, getValues, setValue, formState } = useFormContext();
  const { userType } = useSelector(selectEcommerceUser);
  const { userId, productId } = useSelector(selectProductTaxesParams);
  const product = useSelector(selectCurrentProduct);
  const billingInfo = useSelector(selectBillingInfo);
  const productTaxes = useSelector(selectProductTaxes);
  const [generalError, setGeneralError] = useState(null);
  const [identificationOpts, setIdentificationOpts] = useState([]);
  const [withUpgrade, setWithUpgrade] = useState(false);
  const [installmentsOpts, setInstallmentsOpts] = useState(null);
  const [formAttributes, setFormAttributes] = useState(initialFormAttributes);
  const { currentPlan, eligibleForUpgrade, products } = useSelector(Step4Module1Selectors.getStep4Module1State);
  const {
    propertySubTypes,
    propertyTypes,
    formValues: { selectedOperationTypeId, selectedPropertyTypeId, selectedPropertySubTypeId }
  } = useSelector(Step1Module1Selectors.getStep1Module1State);

  const formRef = useRef(null);
  const { setPreventTransition } = useStepperCtx();
  const currentPlanId = eligibleForUpgrade && currentPlan && withUpgrade ? currentPlan.publication_plan_id : '';
  const { operationTypeId } = useSelector(Step1Module1Selectors.getInfoEvents);
  const propertyObj = { list: propertyTypes, id: selectedPropertyTypeId };
  const subPropertyObj = { list: propertySubTypes, id: selectedPropertySubTypeId };
  usePublisherRedirection(!productId, '/planes/seleccion');

  useEffect(() => {
    if (eligibleForUpgrade && products.length > 0 && !withUpgrade) {
      setWithUpgrade(getWithUpgrade(products));
    }
  }, [products, eligibleForUpgrade, withUpgrade]);

  const notifyAbandon = () => {
    if (productId) {
      track(FORM_ABANDON_EVENT, productId);
    }
  };

  useEffect(() => {
    if (formState.isDirty) {
      setPreventTransition(formRef);
    } else {
      setPreventTransition(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.isDirty]);

  useEffect(() => {
    if (Object.keys(billingInfo).length === 0) {
      dispatch(getEcommerceUser());
    }
    window.addEventListener('beforeunload', notifyAbandon);
    return () => {
      window.removeEventListener('beforeunload', notifyAbandon);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (productId && userType) {
      dispatch(getProductTaxes(userId, { products: productId, userType, currentPlanId, postingId }));
    }
  }, [dispatch, productId, userId, userType, currentPlanId, postingId]);

  useEffect(() => {
    let mounted = true;
    function getIdentifications() {
      try {
        const data = getIdentificationTypes(siteId);
        if (mounted) {
          setIdentificationOpts(data);
        }
      } catch (error) {
        devLogger.error(error);
        const { errorFieldsEnum, message } = error.response.data;
        if (errorFieldsEnum && errorFieldsEnum === 'general') {
          setGeneralError(message);
        }
      }
    }

    async function requestPublicToken() {
      try {
        const { data } = await axios.get(`${API_ECOM}/ecommerce/api/v1/payments/token`, { headers });
        Mercadopago.setPublishableKey(data);
        getIdentifications();
      } catch (error) {
        devLogger.error(error);
        if (error?.message && error?.stack) {
          return;
        }
        const { errorFieldsEnum, message } = error.response.data;
        if (errorFieldsEnum && errorFieldsEnum === 'general') {
          setGeneralError(message);
        }
      }
    }

    requestPublicToken();
    sendCheckoutDataLayer(product);

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (productTaxes && productTaxes.error) {
      const { errorFieldsEnum: field, message } = productTaxes.error;
      if (field !== 'general') {
        setError(field, { message });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTaxes]);

  /**
   * Mapea los errores de verificación de MP a los del formulario
   * y los muestra
   * @param {RequestResponse} MPErrorRequestResponse
   */
  const setErrorMP = ({ cause = [] }) => {
    cause.forEach(({ code }) => {
      const field = get(MPErrorCodes, code, null);
      if (field) {
        setError(field, { type: code, message: `publisher.step4.module2.field.${field}.error` });
      }
    });
  };

  const handleCheckoutError = error => {
    const { code, message, errorFieldsEnum } = error.response.data;
    if (!errorFieldsEnum && code === 500) {
      return push({
        pathname: '/publicador/planes/pago/error',
        search: `?postingId=${postingId}`
      });
    }
    if (errorFieldsEnum === 'general') {
      return setGeneralError(message);
    }
    return setError(errorFieldsEnum, { message });
  };

  const handleCheckoutSuccess = data => {
    data.productId = product.id;
    Cookies.set(`selectedPlan${postingId}`, `${product.publicationPlanId}_0_${product.type.toLowerCase()}`, {
      expires: 1
    });
    push({
      pathname: '/publicador/planes/pago/exito',
      search: `?postingId=${postingId}`,
      state: data
    });
  };

  /**
   * @param {Number} cardNumber CreditCard number
   * @return {Number|String} Six first digits otherwise returns an empty string
   */
  const getBin = (cardNumber = '') => {
    const parsed = String(cardNumber).replace(/[^0-9]/, '');
    if (parsed.length < 6) {
      return '';
    }
    return parsed.slice(0, 6);
  };

  /**
   * Carga las cuotas disponibles con la tarjeta ingresada
   * @param {HttpStatus} status
   * @param {RequestResponse} response
   */
  const showInstallments = (status, response) => {
    if (status === 200) {
      setIssuerFromResponse(response, setFormAttributes);
      const { payer_costs: payerCosts } = response[0];
      const options = {};
      payerCosts.forEach(option => {
        const { recommended_message: recommendedMessage, installments, labels = [] } = option;
        const [cft] = labels;
        options[installments] = React.createElement(
          'option',
          {
            value: installments,
            'data-tax': cft
          },
          recommendedMessage
        );
      });
      setInstallmentsOpts(options);
    } else {
      setInstallmentsOpts(null);
    }
  };

  /**
   * Setea los datos de issuer a partir
   * de un conjunto de issuers
   * @param {Array} issuers
   */
  const showCardIssuers = (issuers = []) => {
    if (issuers) {
      const { id, name } = issuers[0];
      setFormAttributes(previous => ({ ...previous, issuerId: id, issuerName: name }));
    }
  };

  /**
   * Setea los datos de issuer a partir
   * de una colección de datos de cuotas disponibles
   * @param {HttpStatus} status
   * @param {Array} installments
   */
  const showIssuersByInstallment = (status, installments) => {
    const issuers = [];
    if (installments) {
      installments.forEach(({ issuer }) => issuers.push(issuer));
    }
    showCardIssuers(issuers);
  };

  /**
   * Callback para manejar la respuest del SDK de MP
   * al ingresar una tarjeta de crédito y obtener los datos
   * de cuotas, issuers, etc
   *
   * @param {HttpStatus} status
   * @param {RequestResponse} response
   */
  const paymentMethodCallback = (status, response) => {
    if (status === 200) {
      const { id, aditional_info_needed: aditionalInfo } = response[0];
      setFormAttributes(previous => ({ ...previous, paymentMethodId: id }));
      const amount = productTaxes.total;
      const card = getValues('cardNumber');
      const bin = getBin(card);
      Mercadopago.getInstallments(
        {
          payment_method_id: id,
          bin,
          amount
        },
        showInstallments
      );
      const issuerMandatory = includes(aditionalInfo, 'issuer_id');
      if (issuerMandatory) {
        Mercadopago.getInstallments(
          {
            payment_method_id: id,
            bin,
            amount
          },
          showIssuersByInstallment
        );
      }
    }
  };

  /**
   * Handler que maneja los cambios en el campo de número de tarjeta,
   * hace el llamado para traer datos necesarios especificados
   * en @paymentMethodCallback
   */
  const guessingPaymentMethod = () => {
    const card = getValues('cardNumber');
    const cardArr = card.replace(/\s/g, '').match(/\d{1,4}/g);
    const result = cardArr ? cardArr.join(' ') : '';
    setValue('cardNumber', result);

    const bin = getBin(card);
    if (bin) {
      Mercadopago.getPaymentMethod(
        {
          bin
        },
        paymentMethodCallback
      );
    }
  };

  /**
   * Callback que maneja la respuesta al generarse un token de pago via SDK MP,
   * e intenta generar una compra y mandar el mail en caso de éxito
   * @param {FormData} formData
   */
  const CreatePaymentTokenCallback = formData => async (status, response) => {
    const { id: token } = response;
    const utmPayment = '?utm_source=publicador';

    const upgradeInfo =
      eligibleForUpgrade && withUpgrade
        ? {
            postingId,
            realEstateTypeId: selectedPropertyTypeId,
            realEstateOperationTypeId: selectedOperationTypeId,
            productId,
            currentPlanId: currentPlan.publication_plan_id
          }
        : null;
    const paymentData = { ...formData, token, coupon: productTaxes.coupon, upgradeInfo };
    if (status !== 200 && status !== 201) {
      setErrorMP(response);
      dispatch(setPaymentLoading(false));
      return;
    }
    try {
      const { data } = await axios.post(`${API_ECOM}/ecommerce/api/v1/payments/${userType}${utmPayment}`, paymentData, {
        headers,
        timeout: 240000,
        params: { postingId }
      });
      dispatch(setPaymentLoading(false));
      handleCheckoutSuccess(data);
    } catch (error) {
      dispatch(setPaymentLoading(false));
      handleCheckoutError(error);
    }
  };

  const doPayment = async (form, paymentData) => {
    Mercadopago.clearSession();
    Mercadopago.createToken(form, CreatePaymentTokenCallback(paymentData));
  };

  /**
   * Submit Handler al intentar procesar el formulario,
   * limpia la session de MP para evitar errores de tokens
   * en caso de cambiar de tarjeta
   * @param {Object} data
   * @param {ReactSyntheticEvent<SubmitEvent>} evt
   */

  const postBillingData = async data => {
    let res = { status: 200 };
    if (!billingInfo.idClienteSap) {
      try {
        const billingData = getBillingInfo(data);
        res = await axios.post(`${API_ECOM}/ecommerce/api/v1/billings`, billingData, { headers });
      } catch (error) {
        const { code } = error.response.data;
        res.status = code;
        dispatch(setPaymentLoading(false));
        handleCheckoutError(error);
      }
    }
    return res;
  };

  const createOrUpdateBillingInfo = async (data, evt) => {
    const price = product.formatUnitPrice ? currencyOptionsBySite(siteId)[0].name + product.formatUnitPrice : undefined;
    sendEvent('Planes Modulo2', 'Pagar', operationTypeId, propertyObj, subPropertyObj, product.name, price);
    dispatch(setPaymentLoading(true));
    const form = evt.target;
    const installments = product.billingFrequencyId === '57' ? data.installments : 1;
    const { issuerId: issuer, issuerName, paymentMethodId } = formAttributes;
    const { email, areacode, phoneNumber, cardholderName, coupon = '', ...billingData } = data;
    const { status } = await postBillingData(billingData);
    try {
      if (status === 200) {
        const paymentData = {
          email,
          areacode,
          phoneNumber,
          paymentMethodId,
          installments,
          frequencyId: product.billingFrequencyId,
          issuer,
          idProducts: [product.id],
          token: '',
          cardholder: cardholderName,
          transactionAmount: productTaxes.total,
          issuerName,
          coupon
        };
        await doPayment(form, paymentData);
      }
    } catch ({ response }) {
      dispatch(setPaymentLoading(false));
      handleCheckoutError(response);
    }
  };

  return (
    <form onSubmit={handleSubmit(createOrUpdateBillingInfo)} name='pay' id='step4Module2Form' noValidate ref={formRef}>
      <BillingSection />
      <PaymentSection
        guessingPaymentMethod={guessingPaymentMethod}
        identificationOpts={identificationOpts}
        generalError={generalError}
      />
    </form>
  );
}

export default PaymentForm;
